import type { UsageRow } from "../api/types.js";
export type Interval = UsageRow["intervals"][number];
export const duration = (ms: number) => {
  const seconds = Math.floor(Math.max(0, ms) / 1000);
  if (seconds < 60) return `${seconds} sec`;
  const minutes = Math.floor(seconds / 60);
  return minutes < 60 ? `${minutes} min ${seconds % 60} sec` : `${Math.floor(minutes / 60)} hr ${minutes % 60} min`;
};
export function selectHistory(rows: Interval[], from: number, to: number, search: string, confidence: string, name: (pkg: string | null) => string) {
  return rows.flatMap(row => {
    const start = Math.max(from, Date.parse(row.started_at)), end = Math.min(to, Date.parse(row.ended_at));
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start ||
      (confidence !== "all" && row.confidence !== confidence) ||
      !`${name(row.package_id)} ${row.package_id ?? ""}`.toLowerCase().includes(search.trim().toLowerCase())) return [];
    return [{ ...row, started_at: new Date(start).toISOString(), ended_at: new Date(end).toISOString(), ms: end - start }];
  });
}
export function gameTotals(rows: ReturnType<typeof selectHistory>, name: (pkg: string | null) => string) {
  const totals = new Map<string, { key: string; name: string; ms: number; latest: number }>();
  for (const row of rows) {
    if (row.confidence === "unknown") continue;
    const key = row.package_id ?? "", existing = totals.get(key) ?? { key, name: name(row.package_id), ms: 0, latest: 0 };
    existing.ms += row.ms; existing.latest = Math.max(existing.latest, Date.parse(row.ended_at)); totals.set(key, existing);
  }
  return [...totals.values()];
}

// Inventory is already restricted by the API to non-system applications. Keep
// management tools out of playtime; keep Browser as a clearly named content app.
const contentLabels: Record<string,string> = {"com.oculus.browser":"Browser", "com.oculus.igvr":"Instagram", "com.oculus.facebook":"Facebook", "com.oculus.tv":"Meta TV", "com.facebook.horizon":"Horizon Worlds", "com.oculus.cinema":"Video", "com.LuckyVR.PokerStarsVR":"Vegas Infinite", "com.ContinuumXR.UG":"UG", "com.TrassGames.Yeeps":"Yeeps"};
export function isPlayApp(pkg: string | null, apps: {package_id:string}[], source?: string) {
  if (!pkg) return false;
  if (contentLabels[pkg]) return true;
  if (["android", "com.android.", "com.oculus.", "com.meta.", "horizonos.", "horizon.", "dev.questguardian.", "moe.shizuku."].some(prefix=>pkg.startsWith(prefix))) return false;
  return source === "helper_sample" || apps.some(app => app.package_id === pkg);
}
export function appName(pkg: string | null, apps: {package_id:string;display_name:string|null}[]) {
  const display = apps.find(a=>a.package_id===pkg)?.display_name;
  if (display && display !== pkg) return display;
  const labels: Record<string,string> = {...contentLabels,"com.oculus.vrshell":"Quest home", "com.oculus.systemux":"Quest menus", "com.oculus.store":"Meta Store", "com.oculus.accountscenter":"Meta account settings"};
  return labels[pkg || ""] || pkg || "Unidentified activity";
}
